import Vue from 'vue';
import type { Route } from 'vue-router';
import { AppConstants } from '@/types/appConstants';
import CookiesStore from '@/components/ui/cookies/cookies.store';

export enum BannerKey {
  UnderConstruction,
  Discontinued,
  BackendUser
}

interface ILocalConfigState {
  leftDrawerIsMinified: boolean;
  disableDeleteNotePromptTime: Date | null;
  backendUserActive: number;
  showAsStudent: boolean;
  isCurrentPageTeacherMenu: boolean;
  isCurrentPageChildOfTeacherMenu: boolean;
  tocIsRouting: boolean;
  frontPageId: number;
  hideNav: boolean;
  isFrontPage: boolean;
  isIpAccess: boolean;
  loggedInNoAccess: boolean;
  isStaticPage: boolean;
  bannersToHide: Array<{ bannerType: BannerKey }>;
  underConstructionBannerHidden: boolean;
  discontinuedBannerHidden: boolean;
  signInUrl: string;
  // pageTransitionReady tells us when the page transition for content in _.vue is done
  // If it is true, we can expect the content to also be visible
  pageTransitionReady: boolean;
  isEbookPage: boolean;
}

const state: ILocalConfigState = Vue.observable({
  leftDrawerIsMinified: false,
  disableDeleteNotePromptTime: null,
  backendUserActive: 0,
  showAsStudent: false,
  isCurrentPageTeacherMenu: false,
  isCurrentPageChildOfTeacherMenu: false,
  tocIsRouting: false,
  frontPageId: 0,
  hideNav: false,
  isFrontPage: false,
  isIpAccess: false,
  loggedInNoAccess: false,
  isStaticPage: false,
  bannersToHide: [],
  underConstructionBannerHidden: false,
  discontinuedBannerHidden: false,
  signInUrl: '',
  pageTransitionReady: false,
  isEbookPage: false
});

class LocalConfigStore {
  public get pageTransitionReady(): boolean {
    return state.pageTransitionReady;
  }

  public get isEbookPage(): boolean {
    return state.isEbookPage;
  }

  public set isEbookPage(val: boolean) {
    state.isEbookPage = val;
  }

  public set pageTransitionReady(val: boolean) {
    state.pageTransitionReady = val;
  }

  public get signInUrl(): string {
    return state.signInUrl;
  }

  public set signInUrl(url: string) {
    state.signInUrl = url;
  }

  public get isStaticPage(): boolean {
    return state.isStaticPage;
  }

  public set isStaticPage(val: boolean) {
    state.isStaticPage = val;
  }

  public get underConstructionBannerHidden(): boolean {
    return state.underConstructionBannerHidden;
  }

  public set underConstructionBannerHidden(
    underConstructionBannerHidden: boolean
  ) {
    state.underConstructionBannerHidden = underConstructionBannerHidden;
  }

  public get discontinuedBannerHidden(): boolean {
    return state.discontinuedBannerHidden;
  }

  public set discontinuedBannerHidden(discontinuedBannerHidden: boolean) {
    state.discontinuedBannerHidden = discontinuedBannerHidden;
  }

  public get bannersToHide(): Array<{ bannerType: BannerKey }> | null {
    const banners = localStorage.getItem(AppConstants.BANNERS_TO_HIDE);

    if (banners) {
      banners.trim();
      return JSON.parse(banners);
    }

    return null;
  }

  public get isIpAccess(): boolean {
    return state.isIpAccess;
  }

  public set isIpAccess(val: boolean) {
    state.isIpAccess = val;
  }

  public get loggedInNoAccess(): boolean {
    return state.loggedInNoAccess;
  }

  public set loggedInNoAccess(val: boolean) {
    state.loggedInNoAccess = val;
  }

  public get isFrontPage(): boolean {
    return state.isFrontPage;
  }

  public set isFrontPage(val: boolean) {
    state.isFrontPage = val;
  }

  public get hideNav(): boolean {
    return state.hideNav;
  }

  public set hideNav(val: boolean) {
    state.hideNav = val;
  }

  public get frontPageId(): number {
    return state.frontPageId;
  }

  public set frontPageId(val: number) {
    state.frontPageId = val;
  }

  public get tocIsRouting(): boolean {
    return state.tocIsRouting;
  }

  public set tocIsRouting(val: boolean) {
    state.tocIsRouting = val;
  }

  public get backendUserActive(): number {
    return state.backendUserActive;
  }

  public set backendUserActive(val: number) {
    state.backendUserActive = val;
  }

  public get isCurrentPageTeacherMenu(): boolean {
    return state.isCurrentPageTeacherMenu;
  }

  public get isCurrentPageChildOfTeacherMenu(): boolean {
    return state.isCurrentPageChildOfTeacherMenu;
  }

  public get showAsStudent(): boolean {
    return state.showAsStudent;
  }

  public get leftDrawerIsMinified(): boolean {
    return state.leftDrawerIsMinified;
  }

  get disableDeleteNotePromptTime(): Date | null {
    return state.disableDeleteNotePromptTime
      ? state.disableDeleteNotePromptTime
      : null;
  }

  public set isCurrentPageTeacherMenu(isCurrentPageTeacherMenu: boolean) {
    state.isCurrentPageTeacherMenu = isCurrentPageTeacherMenu;
  }

  public set isCurrentPageChildOfTeacherMenu(
    isCurrentPageChildOfTeacherMenu: boolean
  ) {
    state.isCurrentPageChildOfTeacherMenu = isCurrentPageChildOfTeacherMenu;
  }

  public determineIfFrontPage(route: Route): void {
    if (route.path === '/') {
      state.isFrontPage =
        !route.query.id ||
        (!!route.query.id && route.query.id === state.frontPageId.toString());
    }
  }

  public updateIsMinifiedState(isMinified: boolean): void {
    state.leftDrawerIsMinified = isMinified;
  }

  public setDisableDeleteNotePromptTime(timeStamp: Date): void {
    state.disableDeleteNotePromptTime = timeStamp;
  }

  public setBackendUserState(
    backendUser: number,
    forceClose: boolean = false
  ): void {
    const preferPromptHidden = localStorage.getItem(
      AppConstants.LOCAL_STORAGE_HIDE_EDIT_PROMPT
    );

    if (preferPromptHidden) {
      return;
    }

    state.backendUserActive = backendUser || 0;

    if (forceClose) {
      // Do not save to localStorage if functional cookies are not accepted
      if (!CookiesStore.functionalCookiesAllowed) {
        return;
      }

      localStorage.setItem(
        AppConstants.LOCAL_STORAGE_HIDE_EDIT_PROMPT,
        String(backendUser)
      );
    }
  }

  public setShowAsStudent(showAsStudent: boolean): void {
    state.showAsStudent = showAsStudent;
  }

  public updateBannersToHide(banner: BannerKey): void {
    const item = { bannerType: banner };
    state.bannersToHide.push(item);

    // Do not save to localStorage if functional cookies are not accepted
    if (!CookiesStore.functionalCookiesAllowed) {
      return;
    }

    localStorage.setItem(
      AppConstants.BANNERS_TO_HIDE,
      JSON.stringify(state.bannersToHide)
    );
  }
}

export default new LocalConfigStore();
